import React, { useEffect } from "react";
import "../../styles/LegalPages.css";

export default function TermsConditions() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page-shell">
      <div className="legal-page-container">
        <header className="legal-hero-card">
          <div className="legal-top-badge-row">
            <span className="legal-badge-pill">Agreement</span>
            <span className="legal-last-updated">Effective: July 1st, 2026</span>
          </div>
          <h1 className="legal-hero-title">Terms & Conditions</h1>
          <p className="legal-hero-subtitle">
            Welcome to Creators Dost. Creators Dost is an online learning platform offering educational courses, tutorials, training programs, and learning resources primarily for new content creators, video editors, aspiring digital creators, and learners interested in digital content creation. By accessing, registering on, purchasing from, or using the Platform, you agree to comply with these Terms & Conditions.
          </p>
        </header>

        <nav className="legal-switcher-bar">
          <a href="#privacy-policy" className="legal-switch-btn">Privacy Policy</a>
          <a href="#terms-and-conditions" className="legal-switch-btn active">Terms & Conditions</a>
          <a href="#refund-policy" className="legal-switch-btn">Return & Refund</a>
        </nav>

        <div className="legal-clauses-stack">
          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">01</span>
              <h3>Acceptance of Terms</h3>
            </div>
            <p>By using Creators Dost, you confirm that:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> You have read and understood these Terms.</li>
              <li><span className="dot">✓</span> You agree to be legally bound by these Terms.</li>
              <li><span className="dot">✓</span> The information provided by you is accurate.</li>
              <li><span className="dot">✓</span> You will use the Platform only for lawful purposes.</li>
            </ul>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">02</span>
              <h3>User Account</h3>
            </div>
            <p>Some features may require you to create an account. You are responsible for:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Providing accurate information</li>
              <li><span className="dot">✓</span> Maintaining the confidentiality of your login credentials</li>
              <li><span className="dot">✓</span> Keeping your account secure</li>
              <li><span className="dot">✓</span> All activities performed through your account</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              You must immediately notify Creators Dost if you believe your account has been accessed without authorization.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">03</span>
              <h3>Course Purchase and Access</h3>
            </div>
            <p>
              Creators Dost provides digital courses and educational content. After successful payment, access to the purchased course may be provided through the Platform according to the applicable course terms.
            </p>
            <p>
              Course access may vary depending on the course, package, subscription, or offer purchased. Users must carefully review the course description, syllabus, duration, features, and access conditions before making a purchase.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">04</span>
              <h3>No Account Sharing</h3>
            </div>
            <p>
              A course purchased by a user is intended for the purchaser's individual use unless otherwise stated. Users must not:
            </p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Share account credentials</li>
              <li><span className="dot">✓</span> Allow another person to use their account</li>
              <li><span className="dot">✓</span> Sell or transfer course access</li>
              <li><span className="dot">✓</span> Provide unauthorized access to course content</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              Creators Dost may suspend or terminate accounts involved in unauthorized sharing without notice or refund.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">05</span>
              <h3>Intellectual Property</h3>
            </div>
            <p>
              All content available on Creators Dost—including videos, course materials, tutorials, images, graphics, presentations, text, logos, designs, downloadable resources, and educational materials—is owned by, licensed to, or legally available to Creators Dost or the respective content owners.
            </p>
            <p>
              Users may access the content for their personal educational purposes only.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">06</span>
              <h3>Prohibited Use</h3>
            </div>
            <p>Users must strictly avoid the following actions:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Copy or reproduce course content without permission</li>
              <li><span className="dot">✓</span> Record course videos for redistribution</li>
              <li><span className="dot">✓</span> Upload our course content to other websites</li>
              <li><span className="dot">✓</span> Sell or redistribute learning materials</li>
              <li><span className="dot">✓</span> Share downloadable resources publicly</li>
              <li><span className="dot">✓</span> Modify or commercially exploit our content</li>
              <li><span className="dot">✓</span> Attempt to bypass Platform security or gain unauthorized access to accounts</li>
              <li><span className="dot">✓</span> Use the Platform for unlawful purposes</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              Any unauthorized use may result in immediate suspension or termination of the account and may lead to legal action where applicable.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">07</span>
              <h3>Course Content & Discontinuation</h3>
            </div>
            <p>
              Creators Dost continuously works to improve its courses and educational materials. Course content, instructors, lessons, schedules, resources, or other features may be updated, modified, replaced, or discontinued where reasonably necessary.
            </p>
            <p>
              Course outcomes may vary from learner to learner. Creators Dost does not guarantee employment, income, business success, social media growth, followers, views, or any specific professional result from completing a course.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">08</span>
              <h3>Payments</h3>
            </div>
            <p>
              All payments must be made through the verified payment methods made available on the Platform. Users are responsible for ensuring that the payment information provided during a transaction is accurate.
            </p>
            <p>
              Once a payment is successfully completed and course access is provided, the purchase shall be governed by our Refund Policy.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">09</span>
              <h3>Refund and Cancellation</h3>
            </div>
            <p>
              Creators Dost provides digital educational products and online courses. A refund facility is <strong>NOT</strong> available for purchased courses or digital products once access has been delivered.
            </p>
            <p>
              Users are therefore advised to carefully review all available course information before making a purchase. For complete details, please refer to the Return & Refund Policy page.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">10</span>
              <h3>Platform Availability</h3>
            </div>
            <p>
              We aim to keep our Platform available and functional; however, temporary interruptions may occur due to maintenance, technical glitches, server issues, network outages, security incidents, third-party provider failures, or circumstances beyond our reasonable control.
            </p>
            <p>
              Creators Dost shall not be held liable for temporary interruptions resulting from such events.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">11</span>
              <h3>User Conduct</h3>
            </div>
            <p>Users agree not to use the Platform to:</p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Violate applicable local or national laws</li>
              <li><span className="dot">✓</span> Harass, abuse, or threaten others</li>
              <li><span className="dot">✓</span> Upload malicious software, bots, or viruses</li>
              <li><span className="dot">✓</span> Attempt unauthorized system access or data scraping</li>
              <li><span className="dot">✓</span> Distribute unapproved or infringing third-party materials</li>
              <li><span className="dot">✓</span> Misuse another person's personal identity or credentials</li>
              <li><span className="dot">✓</span> Interfere with Platform infrastructure and user operations</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              Creators Dost reserves the right to take appropriate disciplinary and legal action against violators.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">12</span>
              <h3>Account Suspension or Termination</h3>
            </div>
            <p>
              Creators Dost may suspend, restrict, or terminate an account if the user violates these Terms, shares credentials, misuses course content, engages in fraudulent actions, or performs activities harmful to the Platform or other users.
            </p>
            <p>
              Termination due to a violation of these Terms does not create any entitlement to a refund or restitution.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">13</span>
              <h3>Third-Party Services</h3>
            </div>
            <p>
              The Platform may link to third-party services such as payment gateways, cloud hosting providers, analytics frameworks, and external platforms. Creators Dost does not govern and is not responsible for the independent policies, availability, security, or practices of external services.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">14</span>
              <h3>Disclaimer</h3>
            </div>
            <p>
              Creators Dost provides educational content strictly for learning and skill-development purposes. Completion of a course does not guarantee:
            </p>
            <ul className="legal-bullet-list">
              <li><span className="dot">✓</span> Employment or career placement</li>
              <li><span className="dot">✓</span> Monetary income or earnings</li>
              <li><span className="dot">✓</span> Freelance projects, gigs, or clients</li>
              <li><span className="dot">✓</span> Social media followers, viral views, or engagement metrics</li>
              <li><span className="dot">✓</span> Commercial business success</li>
              <li><span className="dot">✓</span> Professional accreditation unless specifically explicitly stated</li>
            </ul>
            <p style={{ marginTop: "12px" }}>
              Outcomes rely fundamentally on individual application, hard work, creative skills, portfolio quality, and changing market conditions.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">15</span>
              <h3>Limitation of Liability</h3>
            </div>
            <p>
              To the fullest extent permitted by applicable law, Creators Dost shall not be held liable for any indirect, incidental, special, punitive, or consequential losses arising out of the use or inability to access the Platform, its digital products, or course content.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">16</span>
              <h3>Changes to Terms</h3>
            </div>
            <p>
              Creators Dost reserves the right to modify these Terms & Conditions at any point. Updated versions will be published directly on this page with a revised Effective Date. Continued interaction with the Platform constitutes your binding acceptance of the updated Terms.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">17</span>
              <h3>Governing Law & Jurisdiction</h3>
            </div>
            <p>
              These Terms & Conditions shall be governed by, construed, and interpreted in accordance with the laws of India. Subject to applicable legal processes, any disputes arising under or in connection with these Terms shall be subject exclusively to the jurisdiction of the courts located in New Delhi, India.
            </p>
          </article>

          <article className="legal-clause-card">
            <div className="clause-heading-row">
              <span className="clause-number-icon">18</span>
              <h3>Contact Us</h3>
            </div>
            <p>For questions or notices regarding these Terms & Conditions, please contact us at:</p>
            <p style={{ marginTop: "8px", fontWeight: "700", color: "#38bdf8" }}>
              Creators Dost • Email: contact@creatorsdost.com
            </p>
          </article>
        </div>

        <div className="legal-support-strip">
          <div>
            <p>Have questions about our Terms & Conditions?</p>
            <strong>Our compliance desk is available to assist you.</strong>
          </div>
          <a href="mailto:contact@creatorsdost.com" className="legal-contact-action-btn">
            ✉️ Contact Legal Desk
          </a>
        </div>
      </div>
    </div>
  );
}